import React, { createContext, useContext, useEffect, useState } from "react";
import { identify, reset } from './analytics';
import { syncLanguageFromProfile } from './i18n/config';

type ClientPrincipal = {
  userId: string;
  userDetails: string; // usually email
  identityProvider: string;
  userRoles: string[];
  claims?: { typ: string; val: string }[];
};

type AuthState =
  | { loading: true; user: null }
  | { loading: false; user: ClientPrincipal | null };

const AuthContext = createContext<AuthState>({ loading: true, user: null });

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<AuthState>({ loading: true, user: null });

  useEffect(() => {
    const fetchAuth = async () => {
      try {
        const response = await fetch("/.auth/me", {
          credentials: "include",
        });

        if (!response.ok) {
          // When running locally without SWA, /.auth/me will 404
          setState({ loading: false, user: null });
          return;
        }

        const data = await response.json();
        const principal: ClientPrincipal | null =
          data?.clientPrincipal ?? null;

        if (principal) {
          identify(principal.userId, principal.userDetails, principal.identityProvider);
          syncLanguageFromProfile();
        } else {
          reset();
        }

        setState({ loading: false, user: principal });
      } catch (err) {
        // On any error, just treat as not logged in
        setState({ loading: false, user: null });
      }
    };

    fetchAuth();
  }, []);

  return (
    <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
